import { useState, useMemo, useEffect } from 'react';
import { OnboardingFeatureHighlights } from 'components/onboarding-feature-highlights/onboarding-feature-highlights.component';
import { PrivacyPolicyPage } from 'pages/privacy-policy/privacy-policy.page';
import { AccountSetupPage } from 'pages/account-setup/account-setup.page';
import { OnboardingComplete } from 'components/onboarding-complete/onboarding-complete.component';
import { useShowAuthPrivacyPolicy } from '../use-show-auth-privacy-policy';
import { useFtuxCompletion } from '../use-ftux-completion/use-ftux-completion.hook';
import { useChangeAnnouncementModal } from '../use-change-announcement-modal/use-change-announcement-modal.hook';

export const useOnboarding = () => {
  const { showAuthPrivacyPolicy, isLoading: privacyPolicyIsLoading } =
    useShowAuthPrivacyPolicy();

  const { markModalAsSeen } = useChangeAnnouncementModal();

  const {
    ftuxComplete,
    isLoading: ftuxIsLoading,
    userProfileError,
    markFtuxComplete,
  } = useFtuxCompletion();

  // Assume that the steps are completed by default
  const [stepsCompleted, setStepsCompleted] = useState({
    featureHighlights: true,
    accountSetup: true,
    onboardingComplete: true,
  });

  useEffect(() => {
    // If ftuxComplete is false, set relevant steps to false
    if (ftuxComplete === false) {
      setStepsCompleted({
        featureHighlights: false,
        accountSetup: false,
        onboardingComplete: false,
      });
    } else {
      setStepsCompleted({
        featureHighlights: true,
        accountSetup: true,
        onboardingComplete: true,
      });
    }
  }, [ftuxComplete]);

  const steps = useMemo(
    () => [
      {
        isComplete: stepsCompleted.featureHighlights,
        component: (
          <OnboardingFeatureHighlights
            onOnboardingComplete={() => {
              setStepsCompleted({
                ...stepsCompleted,
                featureHighlights: true,
              });
            }}
          />
        ),
      },
      {
        // Completion check is handled in the useShowAuthPrivacyPolicy hook, not ftux
        isComplete: !showAuthPrivacyPolicy,
        component: <PrivacyPolicyPage />,
      },

      {
        isComplete: stepsCompleted.accountSetup,
        component: (
          <AccountSetupPage
            accountSetupComplete={() => {
              setStepsCompleted({
                ...stepsCompleted,
                accountSetup: true,
              });
            }}
          />
        ),
      },
      {
        isComplete: stepsCompleted.onboardingComplete,
        component: (
          <OnboardingComplete
            onComplete={async () => {
              // Mark FTUX as complete on last step
              await markFtuxComplete();
              // Seed modal-seen so a first-time user is never shown the change announcement.
              await markModalAsSeen();
            }}
          />
        ),
      },
    ],
    [stepsCompleted, showAuthPrivacyPolicy, markFtuxComplete, markModalAsSeen],
  );

  const isLoading = privacyPolicyIsLoading || ftuxIsLoading;

  const currentStep = useMemo(
    () => steps.find((step) => !step.isComplete),
    [steps],
  );

  // If all steps are complete, set isOnboardingComplete to true
  const isOnboardingComplete = !currentStep;

  return {
    currentStep,
    isOnboardingComplete,
    isLoading,
    userProfileError,
  };
};
